import { Subfolder } from "../../redux/interfaces/folder.types";

// Additional participants live in the lightweight "-cja" templates, in the
// secondary-owner "landlord-pf/pm" templates, or in the owner-representative
// templates. Everything else (tenant-pf/pm, guarantor-*, lease-contract-*,
// collateral, ...) is a PRINCIPAL / required subfolder for its role.
//
// This is the reliable discriminator between principal and additional: it is
// derived from the slug, so it does NOT depend on `metadata.person_index`,
// which DocVault computes per-template and therefore collides (the principal
// and the FIRST additional of the same role can both be person_index === 1).
export function isAdditionalSubfolderSlug(slug?: string | null): boolean {
  if (!slug) return false;
  if (slug.endsWith("-cja")) return true;
  if (slug === "landlord-pf" || slug === "landlord-pm") return true;
  if (slug.startsWith("owner-representative")) return true;
  return false;
}

export function isPrincipalSubfolderSlug(slug?: string | null): boolean {
  return Boolean(slug) && !isAdditionalSubfolderSlug(slug);
}

/**
 * Ordinal used to make same-label subfolders distinguishable in the UI.
 *
 * `baseOf` maps a subfolder to the human label the caller would otherwise show
 * (e.g. "Documentos del Inquilino"). Two subfolders collide in the UI exactly
 * when they produce the same base label, so we group by that label and number
 * the members. Principals are ordered first so the principal reliably gets `1`;
 * additionals follow in their existing array order. Numbering is independent of
 * the unreliable `person_index`.
 *
 * Returns `null` when the subfolder's label is unique (group of one) — the
 * caller should then render the plain base label without a number.
 */
export function getSameLabelOrdinal(
  subfolder: Subfolder,
  allSubfolders: Subfolder[],
  baseOf: (sf: Subfolder) => string,
): number | null {
  const base = baseOf(subfolder);
  const group = allSubfolders.filter((sf) => baseOf(sf) === base);
  if (group.length <= 1) return null;

  const sorted = [...group].sort((a, b) => {
    const aAdditional = isAdditionalSubfolderSlug(a.slug) ? 1 : 0;
    const bAdditional = isAdditionalSubfolderSlug(b.slug) ? 1 : 0;
    if (aAdditional !== bAdditional) return aAdditional - bAdditional;
    return allSubfolders.indexOf(a) - allSubfolders.indexOf(b);
  });

  const position = sorted.findIndex((sf) => sf.id === subfolder.id);
  return position === -1 ? null : position + 1;
}
